import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../utils';
import { partnerApiPath } from '../../apiPath';
import { partnerHttpClient } from '../../httpClient';

const CampaignResultStatusSchema = z
  .enum(['COMPLETED', 'INCOMPLETE'])
  .describe('CampaignResultStatusSchema');

export type CampaignResultStatus = z.infer<typeof CampaignResultStatusSchema>;

const CampaignResultCampaignTypeSchema = z
  .enum(['PROPOSAL', 'RECRUITMENT'])
  .describe('CampaignResultCampaignTypeSchema');

export type CampaignResultCampaignType = z.infer<typeof CampaignResultCampaignTypeSchema>;

const CampaignResultDeliveryStatusSchema = z
  .enum(['SHIPPING_READY', 'SHIPPING', 'DELIVERED', 'CANCELLED'])
  .describe('CampaignResultDeliveryStatusSchema');

export type CampaignResultDeliveryStatus = z.infer<typeof CampaignResultDeliveryStatusSchema>;

const CampaignResultContentLinkTypeSchema = z
  .enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP'])
  .describe('CampaignResultContentLinkTypeSchema');

const CampaignResultContentLinkSchema = z
  .object({
    type: CampaignResultContentLinkTypeSchema,
    url: z.string(),
  })
  .describe('CampaignResultContentLinkSchema');

export type CampaignResultContentLink = z.infer<typeof CampaignResultContentLinkSchema>;

const CampaignResultEvaluationReviewTagSchema = z
  .enum([
    'GOOD_CONTENT_QUALITY',
    'FOLLOWED_GUIDELINES',
    'GOOD_PRODUCT_DESCRIPTION',
    'FITS_BRAND_IMAGE',
    'GOOD_CAMPAIGN_PERFORMANCE',
    'FAST_FEEDBACK',
  ])
  .describe('CampaignResultEvaluationReviewTagSchema');

const CampaignResultEvaluationSchema = z
  .object({
    rating: z.number(),
    reviewTags: z.array(CampaignResultEvaluationReviewTagSchema),
    reviewText: z.string().nullable().optional(),
  })
  .describe('CampaignResultEvaluationSchema');

export type CampaignResultEvaluationResponse = z.infer<typeof CampaignResultEvaluationSchema>;

const LocalDateTimeSchema = z
  .union([z.string(), z.tuple([z.number(), z.number(), z.number()]).rest(z.number())])
  .transform((value) => {
    if (typeof value === 'string') return value;
    const [y, mo, d, h = 0, mi = 0, s = 0] = value;
    const date = new Date(y, mo - 1, d, h, mi, s);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
  })
  .describe('LocalDateTimeSchema');

const CampaignResultProfileSnsChannelSchema = z
  .object({
    type: z.string(),
    url: z.string(),
  })
  .describe('CampaignResultProfileSnsChannelSchema');

const CampaignResultProfileSchema = z
  .object({
    curatorId: z.string(),
    nickname: z.string(),
    avatarUrl: z.string().nullable().optional(),
    gender: z.string().nullable().optional(),
    age: z.number().nullable().optional(),
    likeCount: z.number(),
    // NOTE: 결과 탭 프로필 클릭(샵/SNS) 활성화에 필요. 현황 탭과 동일하게 BE 가 노출(ocmp-affiliate #751).
    curatorShopUrl: z.string().nullable().optional(),
    snsChannels: z.array(CampaignResultProfileSnsChannelSchema).default([]),
  })
  .describe('CampaignResultProfileSchema');

export type CampaignResultProfile = z.infer<typeof CampaignResultProfileSchema>;

const CampaignResultRowSchema = z
  .object({
    id: z.number(),
    campaignType: CampaignResultCampaignTypeSchema,
    status: CampaignResultStatusSchema,
    profile: CampaignResultProfileSchema,
    proposedAt: LocalDateTimeSchema.nullable().optional(),
    confirmedAt: LocalDateTimeSchema.nullable().optional(),
    completedAt: LocalDateTimeSchema.nullable().optional(),
    deadline: LocalDateTimeSchema.nullable().optional(),
    deliveryStatus: CampaignResultDeliveryStatusSchema.nullable().optional(),
    submittedContents: z.array(CampaignResultContentLinkSchema),
    evaluation: CampaignResultEvaluationSchema.nullable().optional(),
  })
  .describe('CampaignResultRowSchema');

export type CampaignResultRowResponse = z.infer<typeof CampaignResultRowSchema>;

const GetCampaignResultsRequestSchema = z
  .object({
    campaignId: z.union([z.number(), z.string()]),
    status: CampaignResultStatusSchema,
    page: z.number().int().min(0).optional(),
    size: z.number().int().min(1).max(100).optional(),
  })
  .describe('GetCampaignResultsRequestSchema');

export type GetCampaignResultsRequest = z.infer<typeof GetCampaignResultsRequestSchema>;

const GetCampaignResultsResponseDataSchema = z
  .object({
    list: z.array(CampaignResultRowSchema),
  })
  .describe('GetCampaignResultsResponseDataSchema');

const GetCampaignResultsResponseSchema = createApiResponseSchema(
  GetCampaignResultsResponseDataSchema,
);

export type GetCampaignResultsResponse = z.infer<typeof GetCampaignResultsResponseSchema>;

export const getCampaignResults = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCampaignResultsRequestSchema),
    async ({ campaignId, ...query }: GetCampaignResultsRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.get<GetCampaignResultsResponse>(
          partnerApiPath.getCampaignResults(campaignId),
          query,
        ),
      ),
    validateApiResponse(GetCampaignResultsResponseSchema),
  ),
);
